import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Radio, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';

const fmtBaht = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// เด้งตอนกดบันทึกเงินเดือน เมื่อมีพนักงานที่ "ที่ต้องจ่ายพนักงาน" ติดลบ — บังคับยกยอดทุกคน (ห้ามบันทึกติดลบ)
// แต่ละคนเลือกได้ว่างวดนี้:
//   'zero' = จ่าย 0 → ยกไปงวดหน้า = ยอดติดลบ
//   'pay'  = ยังจ่ายเงินงวดนี้ตามยอดที่ HR กรอก → ยกไปงวดหน้า = ยอดติดลบ + ยอดที่จ่าย
// list = [{ employee_id, name, amount, gross }] (amount = ยอดที่ติดลบ (บวก), gross = ค่าเริ่มต้นของยอดจ่าย =
//   "ที่ต้องจ่ายพนักงาน" แบบไม่นับหัก CRL/หักอื่นๆ — ดู payDefaultOf ใน HrReportV2.js)
// onConfirm(carryList) — [{ employee_id, amount (ยอดยกไปงวดหน้า), pay_now }] ครบทุกคนใน list
// onCancel() — ยกเลิกการบันทึกทั้งหมด (ให้ HR กลับไปตรวจ)
export default function CarryoverPromptDialog({
  open, list = [], nextPeriodLabel, onConfirm, onCancel,
}) {
  // employee_id → { mode: 'zero' | 'pay', pay: string }
  const [choice, setChoice] = useState({});

  useEffect(() => {
    if (open) {
      const init = {};
      list.forEach((r) => {
        init[String(r.employee_id)] = { mode: 'zero', pay: String(round2(Math.max(0, Number(r.gross) || 0))) };
      });
      setChoice(init);
    }
  }, [open, list]);

  const payOf = (r) => {
    const c = choice[String(r.employee_id)];
    if (c?.mode !== 'pay') return 0;
    const v = parseFloat(c.pay);
    return Number.isFinite(v) && v > 0 ? round2(v) : 0;
  };
  const carryOf = (r) => round2((Number(r.amount) || 0) + payOf(r));

  const invalid = list.some((r) => {
    const c = choice[String(r.employee_id)];
    if (c?.mode !== 'pay') return false;
    const v = parseFloat(c.pay);
    return !Number.isFinite(v) || v < 0;
  });

  const totalCarry = list.reduce((s, r) => s + carryOf(r), 0);

  const setMode = (id, mode) => setChoice((p) => ({ ...p, [id]: { ...p[id], mode } }));
  const setPay = (id, pay) => setChoice((p) => ({ ...p, [id]: { ...p[id], pay } }));

  const confirm = () => {
    if (invalid) return;
    onConfirm?.(list.map((r) => ({
      employee_id: String(r.employee_id),
      amount: carryOf(r),
      pay_now: payOf(r),
    })));
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>เงินเดือนติดลบ — ยกยอดไปงวดหน้า</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          พนักงานต่อไปนี้ &quot;ที่ต้องจ่ายพนักงาน&quot; ติดลบในงวดนี้ (หักไม่หมด) — บันทึกติดลบไม่ได้ ระบบจะยกยอดไปลง
          {' '}<b>หักอื่นๆ</b> ของงวดถัดไป{nextPeriodLabel ? ` (${nextPeriodLabel})` : ''} ให้ทุกคน
          เลือกได้ว่างวดนี้จะจ่าย 0 หรือยังจ่ายเงินให้ก่อน (ยอดที่จ่ายจะถูกบวกเข้ายอดยกไปงวดหน้า)
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>พนักงาน</TableCell>
              <TableCell align="right">ยอดติดลบ</TableCell>
              <TableCell>งวดนี้จ่าย</TableCell>
              <TableCell align="right">ยกไปงวดหน้า</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((r) => {
              const id = String(r.employee_id);
              const c = choice[id] || { mode: 'zero', pay: '' };
              const payInvalid = c.mode === 'pay' && !(parseFloat(c.pay) >= 0);
              return (
                <TableRow key={id}>
                  <TableCell>{r.name || r.employee_id}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#b91c1c', whiteSpace: 'nowrap' }}>
                    −{fmtBaht(r.amount)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Box
                        component="label"
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: 1 }}
                      >
                        <Radio size="small" checked={c.mode === 'zero'} onChange={() => setMode(id, 'zero')} />
                        <Typography variant="body2">0</Typography>
                      </Box>
                      <Box component="label" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <Radio size="small" checked={c.mode === 'pay'} onChange={() => setMode(id, 'pay')} />
                        <Typography variant="body2" sx={{ mr: 0.5 }}>จ่าย</Typography>
                      </Box>
                      <TextField
                        size="small"
                        type="number"
                        value={c.pay}
                        disabled={c.mode !== 'pay'}
                        error={payInvalid}
                        onChange={(e) => setPay(id, e.target.value)}
                        inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                        sx={{ width: 130 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {fmtBaht(carryOf(r))}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Box sx={{ mt: 1.5, textAlign: 'right' }}>
          <Typography variant="body2">
            รวมยกไปงวดหน้า: <b>{fmtBaht(totalCarry)}</b> บาท
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>ยกเลิก (กลับไปตรวจ)</Button>
        <Button variant="contained" onClick={confirm} disabled={invalid}>บันทึกเงินเดือน</Button>
      </DialogActions>
    </Dialog>
  );
}
